/**
 * Dialogs behind the document view's actions. Each one calls the real
 * endpoint and reports what the server said — the actions used to show a
 * success toast without calling anything.
 *
 *   SendDocumentModal — email / WhatsApp the issued document (POST …/deliver).
 *   ReasonModal       — a reason + explicit confirm for Void and Write off.
 */
import React, { useEffect, useState } from 'react';
import { Platform, Pressable, Text, TextInput, View } from 'react-native';
import ModalShell from './modalShell';
import DropdownSelect from '../dropdownSelect';
import { useTheme } from '../../context/themeContext';
import { getContact } from '../../services/billing/contact.service';
import { deliverDocument } from '../../services/billing/document.service';

const web = (s: Record<string, unknown>): any => (Platform.OS === 'web' ? s : {});

const FooterButtons: React.FC<{
  onCancel: () => void;
  onConfirm: () => void;
  confirmText: string;
  busyText: string;
  busy: boolean;
  disabled: boolean;
  danger?: boolean;
}> = ({ onCancel, onConfirm, confirmText, busyText, busy, disabled, danger }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const tone = danger ? colors.danger : colors.accent;
  const off = disabled || busy;
  return (
    <>
      <Pressable onPress={onCancel} style={{ height: 38, paddingHorizontal: 16, borderWidth: 1, borderColor: colors.line, borderRadius: 10, justifyContent: 'center', backgroundColor: colors.card, ...web({ cursor: 'pointer' }) }}>
        <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>Cancel</Text>
      </Pressable>
      <Pressable onPress={onConfirm} disabled={off} style={{ height: 38, paddingHorizontal: 18, borderRadius: 10, justifyContent: 'center', backgroundColor: off ? colors.paper3 : tone, ...web({ cursor: off ? 'not-allowed' : 'pointer' }) }}>
        <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: off ? colors.ink4 : '#fff' }}>{busy ? busyText : confirmText}</Text>
      </Pressable>
    </>
  );
};

const CHANNELS: string[][] = [['EMAIL', 'Email'], ['WHATSAPP', 'WhatsApp']];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?\d{8,15}$/;

export const SendDocumentModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  documentId: string;
  documentNumber: string;
  contactId?: string | null;
  onSent: () => void;
}> = ({ visible, onClose, documentId, documentNumber, contactId, onSent }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [channel, setChannel] = useState('EMAIL');
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [contactInfo, setContactInfo] = useState<{ email: string; phone: string }>({ email: '', phone: '' });

  // Pre-fill from the CRM contact, once per open.
  useEffect(() => {
    if (!visible) return;
    setChannel('EMAIL');
    setMessage('');
    setBusy(false);
    setTo('');
    if (!contactId) return;
    let alive = true;
    getContact(contactId)
      .then((c: any) => {
        if (!alive) return;
        const info = { email: c?.email || '', phone: c?.phone || '' };
        setContactInfo(info);
        setTo(info.email);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [visible, contactId]);

  const pickChannel = (v: string) => {
    setChannel(v);
    setTo(v === 'EMAIL' ? contactInfo.email : contactInfo.phone);
  };
  const valid = channel === 'EMAIL' ? EMAIL_RE.test(to.trim()) : PHONE_RE.test(to.replace(/[\s-]/g, ''));

  const send = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await deliverDocument(documentId, {
        channel: channel as 'EMAIL' | 'WHATSAPP',
        to: channel === 'EMAIL' ? to.trim() : to.replace(/[\s-]/g, ''),
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      onSent();
    } catch {
      // the service layer showed the server's reason
    } finally {
      setBusy(false);
    }
  };

  const inp = { borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper, borderRadius: 9, paddingHorizontal: 12, fontFamily: font.sans, fontSize: 13, color: colors.ink, ...web({ outlineStyle: 'none' }) };
  const label = { fontFamily: font.sansMedium, fontSize: 12, color: colors.ink3, marginBottom: 6, fontWeight: '500' as const };
  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title={`Send ${documentNumber}`}
      subtitle="The customer receives the PDF"
      headerTone="accent"
      width={500}
      footer={<FooterButtons onCancel={onClose} onConfirm={send} confirmText="Send" busyText="Sending…" busy={busy} disabled={!valid} />}
    >
      <View style={{ gap: 14 }}>
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ width: 150 }}>
            <Text style={label}>Send by</Text>
            <DropdownSelect value={channel} options={CHANNELS} onChange={pickChannel} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={label}>{channel === 'EMAIL' ? 'Email address' : 'WhatsApp number'}</Text>
            <TextInput value={to} onChangeText={setTo} placeholder={channel === 'EMAIL' ? 'name@company.com' : '+91 98765 43210'} placeholderTextColor={colors.ink4} autoCapitalize="none" keyboardType={channel === 'EMAIL' ? 'email-address' : 'phone-pad'} style={[inp, { height: 40 }]} />
          </View>
        </View>
        <View>
          <Text style={label}>Message (optional)</Text>
          <TextInput value={message} onChangeText={setMessage} multiline placeholder="Thank you for your business." placeholderTextColor={colors.ink4} style={[inp, { minHeight: 72, paddingTop: 10, textAlignVertical: 'top' }]} />
        </View>
        {!!to && !valid && (
          <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.danger }}>
            {channel === 'EMAIL' ? 'Enter a valid email address.' : 'Enter a valid phone number with country code.'}
          </Text>
        )}
      </View>
    </ModalShell>
  );
};

export const ReasonModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  /** Plain-language consequence, shown before the user commits. */
  warning: string;
  reasons: string[][];
  confirmText: string;
  busyText: string;
  onConfirm: (reasonCode: string) => Promise<void>;
}> = ({ visible, onClose, title, subtitle, warning, reasons, confirmText, busyText, onConfirm }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [reason, setReason] = useState(reasons[0]?.[0] ?? '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setReason(reasons[0]?.[0] ?? '');
    setBusy(false);
  }, [visible, reasons]);

  const confirm = async () => {
    if (!reason || busy) return;
    setBusy(true);
    try {
      await onConfirm(reason);
    } catch {
      // the service layer showed the server's reason
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      headerTone="danger"
      width={480}
      footer={<FooterButtons onCancel={onClose} onConfirm={confirm} confirmText={confirmText} busyText={busyText} busy={busy} disabled={!reason} danger />}
    >
      <View style={{ gap: 14 }}>
        <View style={{ padding: 12, borderRadius: 9, borderWidth: 1, borderColor: colors.dangerLine, backgroundColor: colors.dangerSoft }}>
          <Text style={{ fontFamily: font.sans, fontSize: 12.5, color: colors.danger, lineHeight: 18 }}>{warning}</Text>
        </View>
        <View>
          <Text style={{ fontFamily: font.sansMedium, fontSize: 12, color: colors.ink3, marginBottom: 6, fontWeight: '500' }}>Reason</Text>
          <DropdownSelect value={reason} options={reasons} onChange={setReason} />
        </View>
      </View>
    </ModalShell>
  );
};
